// src/components/station/StationPage.jsx
import React, { useEffect, useState, useContext } from 'react';
import {
  Box, Typography, Button, Table, TableHead, TableRow, TableCell, TableBody,
  Dialog, DialogTitle, DialogContent, TextField, DialogActions,
  IconButton, Switch, FormControlLabel, Chip, Tabs, Tab, Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import RestoreIcon from '@mui/icons-material/Restore';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ScheduleIcon from '@mui/icons-material/Schedule';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import axios from '../../config/axiosInstance';
import { UserContext } from '../context/UserContext';
import useConfirm from '../calendar/dialogs/UseConfirm';
import { StationContext } from '../context/StationContext';
import StationWorkshiftsInline from './StationWorkshiftsInline';
import StationPositionsInline from './StationPositionsInline';
import { useTheme, alpha } from '@mui/material/styles';
import { useSnackbar } from 'notistack';

export default function StationPage() {
  const { enqueueSnackbar } = useSnackbar();
  const { confirm, ConfirmDialog } = useConfirm();
  const theme = useTheme();
  const { permissions, user } = useContext(UserContext);
  const isSuperAdmin = (user?.role || '').toLowerCase() === 'super_admin';
  const { reloadStations } = useContext(StationContext);

  const canRead  = permissions.includes('stations.read');
  const canWrite = permissions.includes('stations.write');
  const canReadStationWorkshifts = permissions.includes('workshifts.read') && permissions.includes('station_workareas.read');

  const [stations, setStations] = useState([]);
  const [open, setOpen]         = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const [name, setName]       = useState('');
  const [address, setAddress] = useState('');
  const [nightHourFrom, setNightHourFrom] = useState('');
  const [nightHourTo, setNightHourTo] = useState('');
  const [showFormErrors, setShowFormErrors] = useState(false);
  const validNightHours = [nightHourFrom, nightHourTo].every(value => /^([01]\d|2[0-3]):[0-5]\d$/.test(value));
  const [rotateAdd, setRotateAdd] = useState(true);

  const [editingStation, setEditingStation] = useState(null);
  const [rotateEdit, setRotateEdit] = useState(true);

  // para abrir/cerrar el panel de configuración inline (una sola estación a la vez)
  const [expandedId, setExpandedId] = useState(null);
  const [activeConfigTab, setActiveConfigTab] = useState(0); // 0 = Turnos, 1 = Rotación por área

  const fetchStations = async () => {
    try {
      const res = await axios.get('station');
      const list = Array.isArray(res.data) ? res.data.map(s => ({
        ...s,
        rotate_shifts: (typeof s.rotate_shifts === 'boolean') ? s.rotate_shifts : true
      })) : [];
      setStations(list);
    } catch (error) {
      console.error('Error al listar estaciones:', error);
      setStations([]);
      enqueueSnackbar('Error al listar estaciones', { variant: 'error' });
    }
  };

  useEffect(() => { if (canRead) fetchStations(); }, [canRead]);

  const validateStationForm = () => {
    setShowFormErrors(true);
    if (!name.trim()) {
      enqueueSnackbar('El nombre de la estación es obligatorio.', { variant: 'warning' });
      return false;
    }
    if (!validNightHours) {
      enqueueSnackbar('Completá las horas de inicio y fin del horario nocturno.', { variant: 'warning' });
      return false;
    }
    return true;
  };

  const handleAdd = async () => {
    if (!validateStationForm()) return;
    try {
      const params = new URLSearchParams({
        name: name.trim(),
        address: (address.trim() || 'S/A'),
        rotate_shifts: String(rotateAdd),
        night_hour_from: nightHourFrom,
        night_hour_to: nightHourTo
      });
      await axios.post(`station/add?${params.toString()}`);
      setOpen(false); setName(''); setAddress(''); setRotateAdd(true);
      await fetchStations(); reloadStations();
      enqueueSnackbar('Estación creada correctamente.', { variant: 'success' });
    } catch (e) { console.error(e); enqueueSnackbar(e?.response?.data?.error || 'Error al crear estación', { variant: 'error' }); }
  };

  const handleEdit = (st) => {
    setEditingStation({ ...st, enabled: typeof st.enabled === 'boolean' ? st.enabled : true });
    setName(st.name || ''); setAddress(st.address || '');
    setNightHourFrom((st.night_hour_from || '').slice(0, 5));
    setNightHourTo((st.night_hour_to || '').slice(0, 5));
    setRotateEdit(typeof st.rotate_shifts === 'boolean' ? st.rotate_shifts : true);
    setShowFormErrors(false);
    setEditOpen(true);
  };

  const handleEditSave = async () => {
    if (!validateStationForm()) return;
    try {
      const params = new URLSearchParams({
        id: editingStation.id,
        name: name.trim(),
        address: (address.trim() || 'S/A'),
        enabled: String(editingStation.enabled),
        rotate_shifts: String(rotateEdit),
        night_hour_from: nightHourFrom,
        night_hour_to: nightHourTo
      });
      await axios.put(`/station/edit?${params.toString()}`);
      setEditOpen(false); setEditingStation(null);
      await fetchStations();
      reloadStations();
      enqueueSnackbar('Estación actualizada correctamente.', { variant: 'success' });
    } catch (e) { console.error(e); enqueueSnackbar(e?.response?.data?.error || 'Error al editar estación', { variant: 'error' }); }
  };

  const handleDisable = async (st) => {
    const ok = await confirm({
      title: 'Deshabilitar estación',
      content: `¿Seguro que querés deshabilitar la estación "${st.name}"?`,
      confirmText: 'Deshabilitar',
    });
    if (!ok) return;
    try {
      await axios.delete('station/delete', { data: { id: st.id } });
      await fetchStations();
      enqueueSnackbar('Estación deshabilitada correctamente.', { variant: 'success' });
    } catch (e) {
      enqueueSnackbar(e?.response?.data?.error || 'Error al deshabilitar estación', { variant: 'error' });
    }
  };

  // Habilitar estación (solo super_admin; el backend también lo exige)
  const handleEnable = async (st) => {
    const ok = await confirm({
      title: 'Habilitar estación',
      content: `¿Seguro que querés habilitar la estación "${st.name}"?`,
      confirmText: 'Habilitar',
    });
    if (!ok) return;
    try {
      const params = new URLSearchParams({ id: st.id, name: st.name, enabled: 'true' });
      await axios.put(`/station/edit?${params.toString()}`);
      await fetchStations();
      reloadStations();
      enqueueSnackbar('Estación habilitada correctamente.', { variant: 'success' });
    } catch (e) {
      enqueueSnackbar(e?.response?.data?.error || 'Error al habilitar estación', { variant: 'error' });
    }
  };

  const sortedStations = [...stations].sort((a, b) => (a.enabled === b.enabled ? 0 : a.enabled ? -1 : 1));

  if (!canRead) {
    return (
      <Box p={4}><Typography variant="h6" color="error">No tienes permiso para ver esta sección.</Typography></Box>
    );
  }

  return (
    <Box sx={{ p: 4 }}>
      {ConfirmDialog}
      <Typography variant="h4" align="center" gutterBottom>Gestión de Estaciones</Typography>

      {canWrite && (
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => {
            setName(''); setAddress(''); setRotateAdd(true);
            setNightHourFrom(''); setNightHourTo('');
            setShowFormErrors(false);
            setOpen(true);
          }}>Agregar estación</Button>
        </Box>
      )}

            <Box display="flex" justifyContent="center">
              <Box width="100%" maxWidth={1000}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Nombre</TableCell>
                      <TableCell>Dirección</TableCell>
                      <TableCell>Estado</TableCell>
                      <TableCell>Rotación</TableCell>
                      <TableCell>Horario nocturno</TableCell>
                      <TableCell align="right">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedStations.map((st) => {
                      const isOpen = expandedId === st.id;
                      const muted = st.enabled === false ? { color: '#aaa', textDecoration: 'line-through' } : {};

                      return (
                        <React.Fragment key={st.id}>
                          {/* Fila principal */}
                          <TableRow
                            hover
                            selected={isOpen}
                            sx={{
                              ...(st.enabled === false ? { backgroundColor: '#f2f2f2' } : {}),
                              ...(isOpen
                                ? {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.06),
                                    '& td:first-of-type': {
                                      borderLeft: `4px solid ${theme.palette.primary.main}`,
                                    },
                                  }
                                : {}),
                            }}
                          >
                            {/* ID */}
                            <TableCell sx={st.enabled === false ? { color: '#aaa' } : {}}>
                              {st.id}
                            </TableCell>

                            {/* Nombre */}
                            <TableCell sx={muted}>{st.name}</TableCell>

                            {/* Dirección */}
                            <TableCell sx={muted}>{st.address}</TableCell>

                            {/* Estado */}
                            <TableCell>
                              {st.enabled ? (
                                <span style={{ color: '#36a16a', fontWeight: 600 }}>Habilitada</span>
                              ) : (
                                <span style={{ color: '#aaa', fontWeight: 600 }}>Deshabilitada</span>
                              )}
                            </TableCell>

                            {/* Rotación */}
                            <TableCell>
                              <Chip
                                size="small"
                                label={st.rotate_shifts ? 'Rota' : 'Fijo'}
                                color={st.rotate_shifts ? 'success' : 'default'}
                                variant={st.enabled ? 'filled' : 'outlined'}
                              />
                            </TableCell>

                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              {st.night_hour_from && st.night_hour_to
                                ? `${st.night_hour_from.slice(0, 5)} a ${st.night_hour_to.slice(0, 5)}`
                                : 'Sin configurar'}
                            </TableCell>

                            {/* Acciones */}
                            <TableCell align="right">
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => setExpandedId(isOpen ? null : st.id)}
                                startIcon={<ScheduleIcon fontSize="small" />}
                                endIcon={
                                  <ExpandMoreIcon
                                    sx={{
                                      transition: 'transform .2s',
                                      transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                    }}
                                  />
                                }
                                sx={{ mr: 1, textTransform: 'none' }}
                                aria-expanded={isOpen ? 'true' : 'false'}
                                disabled={!canReadStationWorkshifts && !canRead}
                              >
                                {isOpen ? 'Ocultar configuración' : 'Configurar estación'}
                              </Button>

                              <IconButton size="small" onClick={() => handleEdit(st)} disabled={!canWrite || !st.enabled}>
                                <EditIcon />
                              </IconButton>
                              <IconButton size="small" title="Deshabilitar" onClick={() => handleDisable(st)} disabled={!canWrite || !st.enabled}>
                                <BlockIcon />
                              </IconButton>
                              {canWrite && st.enabled === false && (
                                <Tooltip title={isSuperAdmin ? 'Habilitar' : 'Solo un super administrador puede habilitar'}>
                                  <span>
                                    <IconButton size="small" disabled={!isSuperAdmin} onClick={() => handleEnable(st)}>
                                      <RestoreIcon />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>

                          {/* Fila expandida con la configuración de la estación */}
                          {isOpen && (
                            <TableRow>
                              <TableCell colSpan={7} sx={{ p: 0 }}>
                                <Box
                                  sx={{
                                    borderLeft: (t) => `4px solid ${t.palette.primary.main}`,
                                    backgroundColor: (t) => alpha(t.palette.primary.main, 0.04),
                                  }}
                                >
                                  <Tabs
                                    value={activeConfigTab}
                                    onChange={(_, v) => setActiveConfigTab(v)}
                                    sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}
                                  >
                                    <Tab
                                      icon={<ScheduleIcon fontSize="small" />}
                                      iconPosition="start"
                                      label="Turnos"
                                      sx={{ textTransform: 'none', minHeight: 48 }}
                                      disabled={!canReadStationWorkshifts}
                                    />
                                    <Tab
                                      icon={<AutorenewIcon fontSize="small" />}
                                      iconPosition="start"
                                      label="Rotación por área"
                                      sx={{ textTransform: 'none', minHeight: 48 }}
                                      disabled={!canRead}
                                    />
                                  </Tabs>
                                  <Box sx={{ p: 2 }}>
                                    {activeConfigTab === 0 ? (
                                      <StationWorkshiftsInline
                                        stationId={st.id}
                                        stationName={st.name}
                                      />
                                    ) : (
                                      <StationPositionsInline stationId={st.id} />
                                    )}
                                  </Box>
                                </Box>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                  
          </Table>
        </Box>
      </Box>

      {/* Modal Agregar */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Nueva estación</DialogTitle>
        <DialogContent>
          <TextField margin="dense" label="Nombre" required fullWidth value={name} onChange={(e) => setName(e.target.value)}
            error={showFormErrors && !name.trim()} helperText={showFormErrors && !name.trim() ? 'El nombre es obligatorio.' : undefined} />
          <TextField margin="dense" label="Dirección" fullWidth value={address} onChange={(e) => setAddress(e.target.value)} />
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <TextField margin="dense" label="Horario nocturno desde" type="time" required fullWidth
              value={nightHourFrom} onChange={(e) => setNightHourFrom(e.target.value)}
              error={showFormErrors && !/^([01]\d|2[0-3]):[0-5]\d$/.test(nightHourFrom)}
              InputLabelProps={{ shrink: true }} inputProps={{ step: 60 }} />
            <TextField margin="dense" label="Horario nocturno hasta" type="time" required fullWidth
              value={nightHourTo} onChange={(e) => setNightHourTo(e.target.value)}
              error={showFormErrors && !/^([01]\d|2[0-3]):[0-5]\d$/.test(nightHourTo)}
              InputLabelProps={{ shrink: true }} inputProps={{ step: 60 }} />
          </Box>
          <Typography variant="caption" color="text.secondary">
            Completá ambas horas. El horario puede cruzar medianoche, por ejemplo, de 22:00 a 06:00.
          </Typography>
          <Box sx={{ mt: 1 }}>
            <FormControlLabel
              control={<Switch checked={rotateAdd} onChange={(e) => setRotateAdd(e.target.checked)} />}
              label="Rotar turnos por defecto (las áreas pueden tener su propia configuración)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button onClick={handleAdd} variant="contained">Crear</Button>
        </DialogActions>
      </Dialog>

      {/* Modal Editar */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Editar estación</DialogTitle>
        <DialogContent>
          <TextField margin="dense" label="Nombre" required fullWidth value={name} onChange={(e) => setName(e.target.value)}
            error={showFormErrors && !name.trim()} helperText={showFormErrors && !name.trim() ? 'El nombre es obligatorio.' : undefined} />
          <TextField margin="dense" label="Dirección" fullWidth value={address} onChange={(e) => setAddress(e.target.value)} />
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <TextField margin="dense" label="Horario nocturno desde" type="time" required fullWidth
              value={nightHourFrom} onChange={(e) => setNightHourFrom(e.target.value)}
              error={showFormErrors && !/^([01]\d|2[0-3]):[0-5]\d$/.test(nightHourFrom)}
              InputLabelProps={{ shrink: true }} inputProps={{ step: 60 }} />
            <TextField margin="dense" label="Horario nocturno hasta" type="time" required fullWidth
              value={nightHourTo} onChange={(e) => setNightHourTo(e.target.value)}
              error={showFormErrors && !/^([01]\d|2[0-3]):[0-5]\d$/.test(nightHourTo)}
              InputLabelProps={{ shrink: true }} inputProps={{ step: 60 }} />
          </Box>
          <Typography variant="caption" color="text.secondary">
            Completá ambas horas. El horario puede cruzar medianoche, por ejemplo, de 22:00 a 06:00.
          </Typography>
          <Box sx={{ mt: 1 }}>
            <FormControlLabel
              control={<Switch checked={rotateEdit} onChange={(e) => setRotateEdit(e.target.checked)} />}
              label="Rotar turnos por defecto (las áreas pueden tener su propia configuración)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancelar</Button>
          <Button onClick={handleEditSave} variant="contained">Guardar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
